import { palette } from '../../styles/tokens';
import { Card } from '../common/Card';
import { useStripeConnect } from '../../hooks/useStripeConnect';
import type { Business } from '../../types';

interface Props {
  business: Business;
}

const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${palette.g100}` };
const LABEL: React.CSSProperties = { fontSize: 13, color: palette.g500, width: 160, flexShrink: 0 };
const VALUE: React.CSSProperties = { fontSize: 13.5, fontWeight: 500, color: palette.g800, flex: 1 };

function Check({ ok }: { ok: boolean }) {
  return <span style={{ color: ok ? palette.green : palette.red, fontSize: 14 }}>{ok ? '\u2713' : '\u2717'}</span>;
}

function OnboardingFeedback({ error, popupBlocked, onRetry }: { error: string | null; popupBlocked: boolean; onRetry: () => void }) {
  return (
    <>
      {error && (
        <div style={{ marginTop: 8, fontSize: 13, color: palette.red }}>
          {error}
        </div>
      )}
      {popupBlocked && (
        <div style={{ marginTop: 8, fontSize: 14, color: palette.g500 }}>
          We couldn't open Stripe in a new tab.{' '}
          <button
            type="button"
            onClick={onRetry}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#635BFF',
              textDecoration: 'underline',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            Open it here instead.
          </button>
        </div>
      )}
    </>
  );
}

// Stripe Direct Charges Spec v1 P4 — non-blocking prompt for legacy Express
// accounts to move onto a Stripe-liable account; payments keep working throughout.
function ReconnectCard({ pending, loading, onStart }: { pending: boolean; loading: boolean; onStart: () => void }) {
  return (
    <div style={{ padding: '16px', background: palette.accentWash, borderRadius: 8, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800, marginBottom: 6 }}>
        {pending ? 'Finishing setup with Stripe' : 'Update your payments account'}
      </div>
      {!pending && (
        <div style={{ fontSize: 13, color: palette.g600, marginBottom: 12, lineHeight: 1.5 }}>
          Stripe needs you to confirm your details once for CrewBit's new payments setup. About 5 minutes. You'll keep taking payments the whole time. Card processing fees now come from Stripe directly, the same as any Stripe account.
        </div>
      )}
      <button
        onClick={onStart}
        disabled={loading}
        style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          background: loading ? palette.g300 : '#635BFF', color: palette.white, fontSize: 14, fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
        }}>
        {loading ? 'Opening Stripe...' : pending ? 'Continue' : 'Update now'}
      </button>
    </div>
  );
}

export function StripeConnectSection({ business }: Props) {
  const connected = business.stripe_connect_onboarding_complete === true;
  const {
    startOnboarding, refreshStatus, loading, popupBlocked, error, retryInCurrentTab,
    needsReconnect, reconnectPending,
  } = useStripeConnect();

  return (
    <Card title="Online Payments — Stripe Connect">
      {needsReconnect && (
        <>
          <ReconnectCard pending={reconnectPending} loading={loading} onStart={() => { void startOnboarding(); }} />
          {connected && <OnboardingFeedback error={error} popupBlocked={popupBlocked} onRetry={retryInCurrentTab} />}
        </>
      )}
      {!connected ? (
        <div>
          <div style={{ fontSize: 13.5, color: palette.g600, marginBottom: 12 }}>
            Connect with Stripe to accept credit card payments on invoices. Money goes directly to your bank account.
          </div>
          <div style={{ padding: '16px', background: palette.accentWash, borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800, marginBottom: 8 }}>Accept Online Payments</div>
            <div style={{ fontSize: 12, color: palette.g500, marginBottom: 12 }}>Set up takes about 5 minutes. You'll need your bank details and ID.</div>
            <button
              onClick={async () => { await startOnboarding(); setTimeout(refreshStatus, 3000); }}
              disabled={loading}
              style={{
                padding: '10px 24px', borderRadius: 8, border: 'none',
                background: loading ? palette.g300 : '#635BFF', color: palette.white, fontSize: 14, fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}>
              {loading ? 'Opening Stripe...' : 'Set Up Stripe'}
            </button>
            <OnboardingFeedback error={error} popupBlocked={popupBlocked} onRetry={retryInCurrentTab} />
          </div>
        </div>
      ) : (
        <div>
          <div style={ROW}>
            <div style={LABEL}>Status</div>
            <div style={VALUE}>
              <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: palette.greenWash, color: palette.green }}>Stripe Connected</span>
            </div>
          </div>
          <div style={ROW}>
            <div style={LABEL}>Charges enabled</div>
            <div style={VALUE}><Check ok={business.stripe_connect_charges_enabled === true} /> {business.stripe_connect_charges_enabled ? 'Yes' : 'No'}</div>
          </div>
          <div style={ROW}>
            <div style={LABEL}>Payouts enabled</div>
            <div style={VALUE}><Check ok={business.stripe_connect_payouts_enabled === true} /> {business.stripe_connect_payouts_enabled ? 'Yes' : 'No'}</div>
          </div>
          {business.stripe_connect_onboarded_at && (
            <div style={{ ...ROW, borderBottom: 'none' }}>
              <div style={LABEL}>Connected since</div>
              <div style={VALUE}>{new Date(business.stripe_connect_onboarded_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 12, color: palette.g400, fontStyle: 'italic' }}>
            Stripe sends payments directly to your bank account. CrewBit charges a 0.5% platform fee.
          </div>
        </div>
      )}
    </Card>
  );
}
